import React from 'react'

import {
  createBatch,
  deleteBatch,
  fetchBatches,
  fetchCourseOptions,
  searchBatchTeachers,
  updateBatch
} from 'api/apartment'

import BatchRosterDrawer from './BatchRosterDrawer'

import ApartmentApiCrudPage from '../shared/ApartmentApiCrudPage'
import { useSelectedApartment } from '../shared/apartmentStore'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const LEVELS = [1, 2, 3]
// Matches the main system's batch STATUS exactly (oh_classes: active/suspended),
// values on the wire, labels on screen.
const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' }
]
const STATUS_LABELS = { active: 'Active', suspended: 'Suspended' }
// A regular batch takes paid enrolments and may hold a few trial sitters;
// a trial batch takes nothing but trials.
const BATCH_TYPES = [
  { value: 'regular', label: 'Regular' },
  { value: 'trial', label: 'Trial' }
]
const TYPE_LABELS = { regular: 'Regular', trial: 'Trial' }

// Mirrors the main system's batch list, minus what an apartment has no notion of
// (centre, batch type, coins, fees — pricing lives on the Pricing page).
const columns = [
  { name: 'courseName', label: 'Course' },
  { name: 'subjectName', label: 'Course Subject' },
  {
    name: 'grades',
    label: 'Course Standard',
    options: {
      filter: false,
      customBodyRender: (value) => (value || []).join(', ') || '—'
    }
  },
  { name: 'displayName', label: 'Batch', options: { filter: false } },
  {
    name: 'batch_type',
    label: 'Batch Type',
    options: { customBodyRender: (v) => TYPE_LABELS[v] || v }
  },
  { name: 'teacherName', label: 'Teacher' },
  { name: 'level', label: 'Batch Level' },
  { name: 'days', label: 'Days', options: { filter: false } },
  { name: 'time', label: 'Time', options: { filter: false } },
  { name: 'enrolled', label: 'Enrolment Count', options: { filter: false } },
  { name: 'capacity', label: 'Enrolment Capacity', options: { filter: false } },
  {
    name: 'seatsAvailable',
    label: 'Seats Left',
    options: { filter: false, customBodyRender: (v) => (v === null ? '—' : v) }
  },
  {
    // capacity - enrolled. Computed, not stored, and not settable: a trial sits
    // in a seat no enrolled child is using. `null` = the batch has no capacity,
    // so nothing limits trials on it.
    name: 'trialSlotsTotal',
    label: 'Trial Slots',
    options: {
      filter: false,
      customBodyRender: (v) => (v === null || v === undefined ? 'Open' : v)
    }
  },
  { name: 'sessionCount', label: 'Sessions', options: { filter: false } },
  // Lifecycle, as in the main system. Whether the batch still has room is the
  // separate `seats` column.
  {
    name: 'status',
    label: 'Batch Status',
    options: { customBodyRender: (value) => STATUS_LABELS[value] || value }
  },
  { name: 'seats', label: 'Seats' }
]

const baseFields = [
  // The course carries the subject and the grade. It is fixed at creation:
  // changing it would change the subject the batch bills against, orphaning
  // its enrolments, sessions and kit charges.
  // A kit is charged once per level, per child, per subject — so moving a batch
  // up a level makes its subject's kit chargeable again.
  { name: 'level', label: 'Batch Level', type: 'select', options: LEVELS },
  { name: 'weekdays', label: 'Days', type: 'multiselect', options: WEEKDAYS },
  { name: 'start_time', label: 'Start Time', type: 'time' },
  { name: 'end_time', label: 'End Time', type: 'time' },
  // Sessions are generated for every selected weekday between these dates —
  // there is no separate Schedule screen.
  { name: 'start_date', label: 'Start Date', type: 'date' },
  { name: 'end_date', label: 'End Date', type: 'date' },
  {
    // Fixed at creation: switching a batch's type after it has enrolments or
    // trials would strand them.
    name: 'batch_type',
    label: 'Batch Type',
    type: 'select',
    options: BATCH_TYPES,
    createOnly: true
  },
  { name: 'capacity', label: 'Max Capacity', type: 'number' },
  {
    // A new batch is always active. Suspending one is an edit, not a choice
    // made at creation — `editOnly` hides it from the create form.
    name: 'status',
    label: 'Batch Status',
    type: 'select',
    options: STATUSES,
    editOnly: true
  }
]

// '16:30:00' -> '16:30' for the <input type="time">.
const toTimeInput = (t) => (t ? String(t).slice(0, 5) : '')

const ApartmentBatches = () => {
  const [apartment] = useSelectedApartment()
  // The batch whose roster drawer is open; null when closed.
  const [rosterBatch, setRosterBatch] = React.useState(null)

  const fetchList = React.useCallback(
    () => fetchBatches(apartment.id),
    [apartment.id]
  )
  const createItem = React.useCallback(
    (payload) => createBatch({ ...payload, apartmentSlug: apartment.id }),
    [apartment.id]
  )

  // Any teacher, searched on the server by name or phone. This used to list
  // only teachers linked to the building, and no teacher is linked to any
  // building, so the dropdown was always empty — see `searchBatchTeachers`.
  const searchTeachers = React.useCallback(
    (text) => searchBatchTeachers(text),
    []
  )

  // The autocomplete is uncontrolled: on edit it needs {uuid, name} to render
  // the teacher already on the batch, or the box looks empty and saving would
  // silently clear the assignment.
  // A function of the row, so editing a batch can show the teacher it already
  // has. The autocomplete is uncontrolled — without `initialValue` the box
  // renders empty and saving would clear the assignment.
  // Scoped to the building, so it cannot be a module-level constant like the
  // rest of `baseFields` — it needs the selected apartment.
  const searchCourses = React.useCallback(
    (text) => fetchCourseOptions(apartment.id, text),
    [apartment.id]
  )

  const fields = React.useCallback(
    (row) => [
      {
        name: 'course',
        label: 'Course (pick one, or type to search)',
        type: 'autocomplete',
        // Loaded up front so the dropdown has options before anyone types.
        fetchOnMount: true,
        fetchValues: searchCourses,
        createOnly: true
      },
      ...baseFields,
      {
        name: 'teacher',
        label: 'Teacher',
        type: 'autocomplete',
        fetchOnMount: true,
        fetchValues: searchTeachers,
        initialValue: row?.teacher
          ? { uuid: row.teacher, name: row.teacherName }
          : null
      }
    ],
    [searchTeachers, searchCourses]
  )

  // `course` is absent: it is createOnly, and the autocomplete is uncontrolled,
  // so seeding it here would set the value without rendering the chosen option.
  const rowToForm = (row) => ({
    level: row.level || 1,
    teacher: row.teacher || '',
    weekdays: row.weekdays || [],
    start_time: toTimeInput(row.start_time),
    end_time: toTimeInput(row.end_time),
    start_date: row.start_date || '',
    end_date: row.end_date || '',
    capacity: row.capacity,
    status: row.status || 'active'
  })

  return (
    <>
      <ApartmentApiCrudPage
        title="Batches"
        tableTitle="Batches"
        entityLabel="Batch"
        fetchList={fetchList}
        createItem={createItem}
        updateItem={updateBatch}
        deleteItem={deleteBatch}
        columns={columns}
        fields={fields}
        rowToForm={rowToForm}
        refreshKey={apartment.id}
        // Clicking a batch shows who is enrolled in it, month by month.
        onRowClick={setRosterBatch}
        emptyValues={{
          course: '',
          level: 1,
          teacher: '',
          weekdays: [],
          start_time: '16:30',
          end_time: '18:00',
          start_date: '',
          end_date: '',
          batch_type: 'regular',
          capacity: 12,
          // Not shown on the create form; the backend defaults to this anyway.
          status: 'active'
        }}
      />
      <BatchRosterDrawer
        open={Boolean(rosterBatch)}
        onClose={() => setRosterBatch(null)}
        batch={rosterBatch}
        apartmentId={apartment.id}
      />
    </>
  )
}

export default ApartmentBatches
